import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  createBulkTask,
  createTask,
  deleteTask,
  fetchQuickTasks,
  fetchTasks,
  updateTask,
  updateTaskStatus,
} from "../../apis/tasks";

import type {
  CreateBulkTaskPayload,
  CreateTaskPayload,
  QuickTask,
  Task,
  TaskStatus,
  UpdateTaskPayload,
} from "../../utils/types/tasks";

export const fetchTasksThunk = createAsyncThunk<
  { data: Task[]; total: number },
  | { search?: string; agentId?: string; vesselStatus?: string; jobId?: string; type?: string }
  | undefined,
  { rejectValue: string }
>("tasks/fetchTasks", async (params, { rejectWithValue }) => {
  try {
    const response = await fetchTasks(params);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch tasks");
  }
});

export const createTaskThunk = createAsyncThunk<
  Task,
  CreateTaskPayload,
  { rejectValue: string }
>("tasks/createTask", async (task, { rejectWithValue }) => {
  try {
    const response = await createTask(task);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create task");
  }
});

export const updateTaskThunk = createAsyncThunk<
  Task,
  UpdateTaskPayload,
  { rejectValue: string }
>("tasks/updateTask", async (task, { rejectWithValue }) => {
  try {
    const response = await updateTask(task);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update task");
  }
});

export const updateTaskStatusThunk = createAsyncThunk<
  Task,
  { taskId: string; status: TaskStatus },
  { rejectValue: string }
>("tasks/updateTaskStatus", async ({ taskId, status }, { rejectWithValue }) => {
  try {
    const response = await updateTaskStatus(taskId, status);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update task status");
  }
});

export const deleteTaskThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("tasks/deleteTask", async (taskId, { rejectWithValue }) => {
  try {
    await deleteTask(taskId);
    return taskId;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to delete task");
  }
});

export const fetchQuickTaskThunk = createAsyncThunk<
  { data: QuickTask[] }
>("tasks/fetchQuickTasks", async (_, { rejectWithValue }) => {
  try {
    const result = await fetchQuickTasks();
    return { data: result };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch Quicktasks");
  }
});

export const createBulkTaskThunk = createAsyncThunk<
  { success: boolean; tasks: Task[] },
  CreateBulkTaskPayload,
  { rejectValue: string }
>("tasks/createBulkTask", async (task, { rejectWithValue }) => {
  try {
    const response = await createBulkTask(task);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create tasks");
  }
});
